import React, { useEffect, useMemo, useState } from "react"
import _ from "lodash"
import { Message, toaster } from "rsuite"
import {
    Button,
    CircularProgress,
    InputAdornment,
    LinearProgress,
    ListItemText,
    MenuItem,
    TextField,
    Tooltip
} from "@mui/material"
import {
    CheckCircle,
    Download,
    EventNoteOutlined,
    GroupsOutlined,
    LinkOutlined,
    RefreshOutlined,
    SchoolOutlined,
    Search,
    SearchOff,
    TaskAltOutlined
} from "@mui/icons-material"

// Still imported: other pages share some of its global class names.
import "./studentAdmit.css"

import { GetCurrentUser, GetUserPrivillages } from "../utils/hooks"
import { Loader } from "../components/index"
import ClassList from "../components/ClassList"
import { useSnackbar } from "../components/Snakebar"
import { downloadLinksPdf } from "../components/LinksPdf"
import EmptyState from "../Lectures/components/EmptyState"
import { initials, saveBlob, sortByRollNo } from "../StockManager/classroom/helpers"

import { useLazyGetInstitutionClassesQuery } from "../Redux/actions/classSetup.action"
import { useLazyGetPlatformRolesQuery } from "../Redux/actions/setting.action"
import {
    useLazyBulkAdmitCardQuery,
    useExaminationAdmitCardMutation,
    useLazyGetAllExamsQuery
} from "../Redux/actions/examAction"
import {
    useLazyGetClassStudentsQuery,
    useUpdateStudentMetadataMutation
} from "../Redux/actions/studentAction"

const toast = (type, message, placement = "bottomCenter") =>
    toaster.push(<Message type={type}>{message}</Message>, { placement })

const NO_PERMISSION = "Not Allow to download the bulk admit card,Please connect with Admin user."

const pillButtonSx = { textTransform: "none", borderRadius: "10px", whiteSpace: "nowrap" }

const STATUS_TABS = [
    { key: "all", label: "All" },
    { key: "approved", label: "Approved" },
    { key: "pending", label: "Pending" }
]

const approvedExams = (student) => {
    const exams = _.get(student, "meta_data.exams", [])
    return Array.isArray(exams) ? exams : []
}

const isApprovedFor = (student, exam) =>
    Boolean(exam) && approvedExams(student).some((item) => item.exam_ref === exam.id)

export default function StudentAdmit() {
    const { showSnackbar } = useSnackbar()

    const [activeClassId, setActiveClassId] = useState(null)
    const [examId, setExamId] = useState("")
    const [statusTab, setStatusTab] = useState("all")
    const [search, setSearch] = useState("")
    const [busy, setBusy] = useState(null) // { id, action }
    const [generatingLinks, setGeneratingLinks] = useState(false)

    const [getClasses, classesState] = useLazyGetInstitutionClassesQuery()
    const [getExams, examsState] = useLazyGetAllExamsQuery()
    const [getRoles, rolesState] = useLazyGetPlatformRolesQuery()
    const [getClassStudents, studentsState] = useLazyGetClassStudentsQuery()
    const [updateStudentMetadata] = useUpdateStudentMetadataMutation()
    const [bulkDownloadApi, bulkDownloadState] = useLazyBulkAdmitCardQuery()
    const [downloadStudentCardApi] = useExaminationAdmitCardMutation()

    /* ------------------------------ data ------------------------------ */

    const classes = useMemo(() => {
        const list = _.get(classesState, "data.list", [])
        return Array.isArray(list) ? list : []
    }, [classesState])

    const exams = useMemo(() => {
        const list = _.get(examsState, "data.list", [])
        return Array.isArray(list) ? list : []
    }, [examsState])

    const activeClass = useMemo(
        () => classes.find((item) => item.id === activeClassId) || null,
        [classes, activeClassId]
    )

    const classExams = useMemo(
        () => (activeClass ? exams.filter((exam) => exam.class_ref === activeClass.id) : []),
        [exams, activeClass]
    )

    const selectedExam = useMemo(
        () => classExams.find((exam) => exam.id === examId) || null,
        [classExams, examId]
    )

    const students = useMemo(() => {
        if (!activeClass) return []
        const list = _.get(studentsState, "currentData.list", [])
        return Array.isArray(list) ? sortByRollNo(list) : []
    }, [activeClass, studentsState])

    const approvedCount = useMemo(
        () => students.filter((student) => isApprovedFor(student, selectedExam)).length,
        [students, selectedExam]
    )

    const visibleStudents = useMemo(() => {
        const term = search.trim().toLowerCase()
        return students.filter((student) => {
            const approved = isApprovedFor(student, selectedExam)
            if (statusTab === "approved" && !approved) return false
            if (statusTab === "pending" && approved) return false
            if (!term) return true
            return [student.name, student.roll_no].some((value) =>
                String(value ?? "").toLowerCase().includes(term)
            )
        })
    }, [students, selectedExam, statusTab, search])

    // Admin and owner may do everything; other roles need "Upload Content"
    const canManage = useMemo(() => {
        const userRole = GetUserPrivillages()
        if (!userRole) return false
        if (userRole === "admin" || userRole === "owner") return true
        if (!rolesState.isSuccess) return false

        const role = _.get(rolesState, "currentData.roles", []).find?.((item) => item.designation === userRole)
        const privileges = _.get(role, "meta_data.privileges", [])
        return Array.isArray(privileges) && privileges.includes("Upload Content")
    }, [rolesState])

    /* ----------------------------- fetching ---------------------------- */

    const fetchData = () => {
        const user_platform = _.get(GetCurrentUser(), "meta_data.user_platform", "")
        if (!user_platform) return
        getClasses({ institution_ref: user_platform })
        getExams({ school_id: user_platform })
        getRoles({ platform_id: user_platform })
    }

    const fetchStudents = (item = activeClass) => {
        if (!item?.id || !item?.institution_ref) return
        getClassStudents({ school_id: item.institution_ref, class_id: item.id })
    }

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const selectClass = (item) => {
        setActiveClassId(item.id)
        setStatusTab("all")
        setSearch("")
        fetchStudents(item)
    }

    // Open the first class once classes arrive, or if the open one disappears
    useEffect(() => {
        if (classes.length > 0 && !activeClass) selectClass(classes[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes])

    // Keep a valid exam selected for the open class: its latest one by default
    useEffect(() => {
        if (!classExams.some((exam) => exam.id === examId)) {
            setExamId(classExams.length > 0 ? classExams[classExams.length - 1].id : "")
        }
    }, [classExams, examId])

    const refresh = () => {
        fetchData()
        fetchStudents()
    }

    /* ------------------------------ actions ----------------------------- */

    const approve = async (student) => {
        if (!selectedExam) return
        if (isApprovedFor(student, selectedExam)) {
            toast("info", "Alrady Approved!", "topCenter")
            return
        }

        const meta_data = {
            ..._.get(student, "meta_data", {}),
            exams: [
                ...approvedExams(student),
                { exam_name: selectedExam.exam_name, exam_ref: selectedExam.id }
            ]
        }

        setBusy({ id: student.id, action: "approve" })
        try {
            const result = await updateStudentMetadata({ user_id: student.id, meta_data }).unwrap()
            toast("success", _.get(result, "message", "User metadata updated success"))
            fetchStudents()
        } catch {
            toast("error", "Error while update the user meta_data!")
        } finally {
            setBusy(null)
        }
    }

    const downloadCard = async (student) => {
        if (!selectedExam || !activeClass) return

        setBusy({ id: student.id, action: "download" })
        try {
            const blob = await downloadStudentCardApi({
                school_id: selectedExam.school_ref,
                class_id: selectedExam.class_ref,
                roll_no: student.roll_no,
                exam_id: selectedExam.id,
                class_name: activeClass.class_name,
                // The API requires a string; the server prints "NAN" for a missing DOB anyway
                dob: student.dob || "NAN",
                call_type: "admin"
            }).unwrap()
            saveBlob(blob, "student_examination_admit.pdf")
        } catch (error) {
            showSnackbar({
                message: _.get(error, "data.detail", "") || "Couldn't download the admit card. Please try again.",
                severity: "error"
            })
        } finally {
            setBusy(null)
        }
    }

    const downloadBulk = async () => {
        if (!canManage) {
            showSnackbar({ message: NO_PERMISSION, severity: "info" })
            return
        }
        if (!selectedExam) return

        try {
            const blob = await bulkDownloadApi({
                school_ref: selectedExam.school_ref,
                class_ref: selectedExam.class_ref,
                exam_ref: selectedExam.id
            }).unwrap()
            saveBlob(blob, "bulk_student_examination_admit.pdf")
        } catch (error) {
            showSnackbar({
                message: _.get(error, "data.detail", "") || "Couldn't download the admit cards. Please try again.",
                severity: "error"
            })
        }
    }

    const downloadLinks = () => {
        if (!canManage) {
            showSnackbar({ message: NO_PERMISSION, severity: "info" })
            return
        }
        if (!selectedExam) return

        const className = activeClass ? activeClass.class_name : ""
        const rows = students.map((item) => ({
            rollNo: item.roll_no,
            name: item.name,
            link: `${window.location.origin}/admit/${item.school_ref}/${item.class_ref}/${selectedExam.id}/${item.id}`
        }))

        setGeneratingLinks(true)
        downloadLinksPdf({
            title: "Student Admit Card Download Links",
            linkHeader: "Admit Card Download Link",
            rows,
            className,
            examName: selectedExam.exam_name,
            fileName: `${className ? className + "_" : ""}${selectedExam.exam_name || ""}_admit_links.pdf`.replace(/\s+/g, "_")
        })
            .catch(() => {
                showSnackbar({ message: "Error while generating PDF, Please try again", severity: "error" })
            })
            .finally(() => setGeneratingLinks(false))
    }

    /* ------------------------------- view ------------------------------- */

    const loadingClasses = classesState.isLoading || classesState.isFetching
    const loadingStudents = studentsState.isFetching
    const blocking = classesState.isLoading || bulkDownloadState.isFetching || generatingLinks

    const examCountFor = (item) => {
        const count = exams.filter((exam) => exam.class_ref === item.id).length
        return `${count} exam${count === 1 ? "" : "s"}`
    }

    const rowActions = (student) => {
        const approved = isApprovedFor(student, selectedExam)
        const working = busy?.id === student.id
        const disabled = !canManage || !selectedExam || Boolean(busy)
        const hint = !canManage
            ? "You don't have permission to manage admit cards"
            : !selectedExam
                ? "Select an examination first"
                : ""

        return (
            <Tooltip title={hint} arrow disableHoverListener={!hint}>
                <div className="flex items-center justify-end gap-1.5">
                    <Button
                        size="small"
                        variant={approved ? "text" : "contained"}
                        color="success"
                        disableElevation
                        startIcon={
                            working && busy.action === "approve"
                                ? <CircularProgress size={14} color="inherit" />
                                : <TaskAltOutlined fontSize="small" />
                        }
                        disabled={disabled || approved}
                        onClick={() => approve(student)}
                        sx={{ ...pillButtonSx, borderRadius: "8px" }}
                    >
                        {approved ? "Approved" : "Approve"}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                            working && busy.action === "download"
                                ? <CircularProgress size={14} />
                                : <Download fontSize="small" />
                        }
                        disabled={disabled}
                        onClick={() => downloadCard(student)}
                        sx={{ ...pillButtonSx, borderRadius: "8px" }}
                    >
                        Card
                    </Button>
                </div>
            </Tooltip>
        )
    }

    const statusBadge = (student) => {
        if (!selectedExam) return <span className="text-xs text-slate-400">-</span>
        return isApprovedFor(student, selectedExam) ? (
            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                <CheckCircle sx={{ fontSize: 13 }} /> Approved
            </span>
        ) : (
            <span className="inline-flex whitespace-nowrap rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                Pending
            </span>
        )
    }

    const avatar = (student) => (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
            {initials(student.name)}
        </span>
    )

    const renderStudents = () => {
        if (studentsState.isError && !loadingStudents) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<GroupsOutlined />}
                        title="Couldn't load students"
                        description="Please check your connection and try again."
                        actionLabel="Retry"
                        onAction={() => fetchStudents()}
                    />
                </div>
            )
        }

        if (loadingStudents && students.length === 0) {
            return (
                <div className="space-y-3 p-5">
                    {[0, 1, 2, 3, 4].map((key) => (
                        <div key={key} className="h-12 animate-pulse rounded-lg bg-slate-100" />
                    ))}
                </div>
            )
        }

        if (students.length === 0) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<GroupsOutlined />}
                        title="No students found"
                        description="No students are currently enrolled in this class."
                    />
                </div>
            )
        }

        if (visibleStudents.length === 0) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<SearchOff />}
                        title="No students match"
                        description="Try a different search or status."
                        actionLabel="Clear filters"
                        onAction={() => {
                            setSearch("")
                            setStatusTab("all")
                        }}
                    />
                </div>
            )
        }

        return (
            <>
                {/* Wide screens */}
                <div className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[720px] text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                <th className="w-20 py-3 pl-5 pr-2 font-semibold">Roll no</th>
                                <th className="px-3 py-3 font-semibold">Student</th>
                                <th className="px-3 py-3 font-semibold">Approved exams</th>
                                <th className="px-3 py-3 font-semibold">Status</th>
                                <th className="py-3 pl-3 pr-5 text-right font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {visibleStudents.map((student) => {
                                const approvedList = approvedExams(student)
                                return (
                                    <tr key={student.id ?? student.roll_no} className="align-middle hover:bg-slate-50/70">
                                        <td className="py-3 pl-5 pr-2">
                                            <span className="inline-flex min-w-[2rem] justify-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-700">
                                                {student.roll_no ?? "-"}
                                            </span>
                                        </td>
                                        <td className="max-w-[260px] px-3 py-3">
                                            <div className="flex min-w-0 items-center gap-3">
                                                {avatar(student)}
                                                <div className="min-w-0">
                                                    <div className="truncate font-medium text-slate-800">{student.name || "-"}</div>
                                                    <div className="truncate text-xs text-slate-500">DOB {student.dob || "not provided"}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-3">
                                            {approvedList.length > 0 ? (
                                                <div className="flex max-w-[260px] flex-wrap gap-1">
                                                    {approvedList.map((exam) => (
                                                        <span
                                                            key={exam.exam_ref}
                                                            className="rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                                                        >
                                                            {exam.exam_name}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-slate-400">None yet</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-3">{statusBadge(student)}</td>
                                        <td className="py-3 pl-3 pr-5">{rowActions(student)}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Phones */}
                <ul className="divide-y divide-slate-100 md:hidden">
                    {visibleStudents.map((student) => (
                        <li key={student.id ?? student.roll_no} className="space-y-3 px-4 py-4">
                            <div className="flex items-start gap-3">
                                {avatar(student)}
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="truncate font-medium text-slate-800">{student.name || "-"}</div>
                                        {statusBadge(student)}
                                    </div>
                                    <div className="truncate text-xs text-slate-500">
                                        Roll {student.roll_no ?? "-"} · DOB {student.dob || "not provided"}
                                    </div>
                                </div>
                            </div>
                            {rowActions(student)}
                        </li>
                    ))}
                </ul>
            </>
        )
    }

    return (
        <div className="mx-auto w-full max-w-[1400px] px-4 pb-24 pt-6 sm:px-6 lg:px-8">

            {/* Page header */}
            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
                        Examination management
                    </p>
                    <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[28px]">
                        Student Admit Cards
                    </h1>
                    <p className="mt-1 max-w-xl text-sm text-slate-500">
                        Pick a class and an examination to approve, download and share admit cards.
                    </p>
                </div>
                <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<RefreshOutlined />}
                    onClick={refresh}
                    disabled={loadingClasses || loadingStudents}
                    sx={{ ...pillButtonSx, borderColor: "#e2e8f0", color: "#334155" }}
                >
                    Refresh
                </Button>
            </header>

            {!loadingClasses && classes.length === 0 ? (
                <EmptyState
                    icon={<SchoolOutlined />}
                    title={classesState.isError ? "Couldn't load classes" : "No classes found"}
                    description={
                        classesState.isError
                            ? "Please check your connection and try again."
                            : "There are currently no classes available for admit card management."
                    }
                    actionLabel={classesState.isError ? "Retry" : undefined}
                    onAction={classesState.isError ? fetchData : undefined}
                />
            ) : (
                <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">

                    {/* Classes */}
                    <aside className="lg:sticky lg:top-4 lg:self-start">
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-sm font-semibold text-slate-700">Classes</h2>
                            <span className="text-xs tabular-nums text-slate-400">{classes.length}</span>
                        </div>
                        <ClassList
                            classes={classes}
                            activeId={activeClassId}
                            onSelect={selectClass}
                            loading={loadingClasses}
                            getMeta={examCountFor}
                        />
                    </aside>

                    {/* Selected class */}
                    <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="h-1">
                            {loadingStudents && <LinearProgress sx={{ height: 3 }} />}
                        </div>

                        <div className="px-4 pt-4 sm:px-5">
                            <h2 className="truncate text-lg font-semibold text-slate-900">
                                {activeClass?.class_name || "Select a class"}
                            </h2>
                            <p className="text-sm text-slate-500">
                                {students.length} student{students.length === 1 ? "" : "s"}
                                {selectedExam && students.length > 0 && (
                                    <span className="text-slate-400">
                                        {" "}· {approvedCount} of {students.length} approved for {selectedExam.exam_name}
                                    </span>
                                )}
                            </p>
                        </div>

                        {/* Examination and bulk actions */}
                        <div className="mx-4 mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:mx-5 sm:p-4">
                            {classExams.length === 0 ? (
                                <div className="flex items-center gap-3 text-sm text-slate-500">
                                    <EventNoteOutlined className="text-slate-400" />
                                    No examinations have been created for this class yet.
                                </div>
                            ) : (
                                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                                    <TextField
                                        select
                                        size="small"
                                        label="Examination"
                                        value={selectedExam ? examId : ""}
                                        onChange={(e) => setExamId(e.target.value)}
                                        sx={{ flex: 1, minWidth: 0, "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" } }}
                                        SelectProps={{ renderValue: () => selectedExam?.exam_name || "" }}
                                    >
                                        {classExams.map((exam) => (
                                            <MenuItem key={exam.id} value={exam.id}>
                                                <ListItemText
                                                    primary={exam.exam_name}
                                                    secondary={exam.created_at ? `Created ${exam.created_at}` : null}
                                                />
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            startIcon={<Download />}
                                            onClick={downloadBulk}
                                            disabled={!selectedExam || bulkDownloadState.isFetching}
                                            sx={pillButtonSx}
                                        >
                                            All admit cards
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<LinkOutlined />}
                                            onClick={downloadLinks}
                                            disabled={!selectedExam || generatingLinks || students.length === 0}
                                            sx={{ ...pillButtonSx, backgroundColor: "#fff" }}
                                        >
                                            Download links
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Status tabs */}
                        <div
                            role="tablist"
                            aria-label="Filter by approval"
                            className="mt-4 flex gap-1 overflow-x-auto border-b border-slate-200 px-4 sm:px-5"
                        >
                            {STATUS_TABS.map((tab) => {
                                const count =
                                    tab.key === "all"
                                        ? students.length
                                        : tab.key === "approved"
                                            ? approvedCount
                                            : students.length - approvedCount
                                const active = statusTab === tab.key
                                return (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        disabled={!selectedExam && tab.key !== "all"}
                                        onClick={() => setStatusTab(tab.key)}
                                        className={[
                                            "-mb-px flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500",
                                            active
                                                ? "border-indigo-600 font-semibold text-indigo-700"
                                                : "border-transparent text-slate-500 hover:text-slate-800"
                                        ].join(" ")}
                                    >
                                        {tab.label}
                                        <span
                                            className={[
                                                "rounded-full px-2 py-0.5 text-xs tabular-nums",
                                                active ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"
                                            ].join(" ")}
                                        >
                                            {selectedExam || tab.key === "all" ? count : "–"}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>

                        {/* Search */}
                        <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
                            <TextField
                                fullWidth
                                size="small"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name or roll number"
                                inputProps={{ "aria-label": "Search students" }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                            />
                        </div>

                        {renderStudents()}
                    </section>
                </div>
            )}

            {blocking && <Loader show={true} />}
        </div>
    )
}
